import { useState, useEffect, useCallback } from "react";
import { PhoneCall, Smartphone, Monitor, Pause } from "lucide-react";
import { callsAPI } from "../../services/api";

const NAVY = "#003366";
const GREEN = "#16a34a";
const POLL_MS = 5000;

function fmt(s) {
  if (s == null) return "";
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
}
function initials(name) {
  return (name || "?").split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
}

// Live "Who's on a call" dispatch strip. `compact` = slim row for the Comms
// dock; full = richer card row for the phone workspace. Polls team-on-call.
export default function TeamOnCallStrip({ compact = false, hideWhenEmpty = false }) {
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const poll = useCallback(async () => {
    try {
      const { data } = await callsAPI.getTeamOnCall();
      setRows(data?.on_call || []);
    } catch { /* keep last */ }
    finally { setLoaded(true); }
  }, []);

  useEffect(() => {
    poll();
    const id = setInterval(poll, POLL_MS);
    return () => clearInterval(id);
  }, [poll]);

  if (!loaded) return null;

  const count = rows.length;

  if (compact) {
    // Slim bar for the Comms dock. Hidden entirely when nobody is on a call.
    if (count === 0) return null;
    return (
      <div className="px-3 py-2 border-b border-slate-200 bg-slate-50" data-testid="team-on-call-strip-compact">
        <div className="flex items-center gap-1.5 mb-1.5">
          <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: GREEN }} />
          <span className="text-[10px] font-black uppercase tracking-wider text-slate-500">On a call · {count}</span>
        </div>
        <div className="flex gap-1.5 overflow-x-auto no-scrollbar pb-0.5">
          {rows.map((r) => (
            <div key={r.call_sid} data-testid={`on-call-chip-${r.user_id || r.call_sid}`}
              className="shrink-0 flex items-center gap-1.5 pl-1 pr-2 py-1 rounded-full bg-white border border-slate-200"
              title={`${r.name} · ${r.caller_number || ""}${r.on_hold ? " · on hold" : ""}`}>
              <span className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-black text-white" style={{ background: NAVY }}>{initials(r.name)}</span>
              <span className="text-[11px] font-semibold text-slate-700 max-w-[90px] truncate">{r.name}</span>
              <span className="text-[10px] tabular-nums text-slate-400">{r.on_hold ? "hold" : fmt(r.seconds)}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Full strip for the phone workspace.
  if (hideWhenEmpty && count === 0) return null;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3 mb-4" data-testid="team-on-call-strip">
      <div className="flex items-center gap-2 mb-2">
        <PhoneCall className="w-4 h-4" style={{ color: GREEN }} />
        <span className="text-[12px] font-black uppercase tracking-wider" style={{ color: NAVY }}>Who's on a call</span>
        <span className="text-[11px] font-bold text-white rounded-full px-2 py-0.5" style={{ background: count ? GREEN : "#94a3b8" }}>{count}</span>
      </div>
      {count === 0 ? (
        <p className="text-[12px] text-slate-400 py-1" data-testid="team-on-call-empty">Nobody's on a call right now.</p>
      ) : (
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
          {rows.map((r) => (
            <div key={r.call_sid} data-testid={`on-call-card-${r.user_id || r.call_sid}`}
              className="shrink-0 w-[188px] rounded-xl border border-slate-200 p-2.5 bg-slate-50">
              <div className="flex items-center gap-2">
                <span className="relative w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-black text-white shrink-0" style={{ background: NAVY }}>
                  {initials(r.name)}
                  <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-slate-50 flex items-center justify-center" style={{ background: r.on_hold ? "#f59e0b" : GREEN }}>
                    {r.device === "desk" ? <Smartphone className="w-2 h-2 text-white" /> : <Monitor className="w-2 h-2 text-white" />}
                  </span>
                </span>
                <div className="min-w-0">
                  <p className="text-[12px] font-bold text-slate-800 truncate">{r.name}</p>
                  <p className="text-[10px] text-slate-400 tabular-nums flex items-center gap-1">
                    {r.on_hold ? <><Pause className="w-2.5 h-2.5" /> on hold</> : fmt(r.seconds)}
                    {r.device ? ` · ${r.device === "desk" ? "desk" : "app"}` : ""}
                  </p>
                </div>
              </div>
              <p className="mt-1.5 text-[11px] text-slate-500 truncate">{r.company_name || r.caller_number || "Caller"}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
